// PDP gallery thumbnails (mobile only).
// Replaces the dots row under the product media with a 3-up thumbnail strip.
// Shipped from the rajani-pdp-gallery-thumbnails-v2 AB test; loaded only on the
// product template by layout/theme.liquid, styles live in pdp-gallery-thumbnails.css.
//
// No markup is added here: snippets/product-media.liquid already renders the
// thumbnails (kept off mobile by .hidden-pocket, since show_thumbnails_on_mobile is
// false) and <flickity-controls> already wires every [data-action="select"] thumbnail
// to the main carousel. The CSS unhides the strip and hides the dots; this only drives
// the strip with Swiper. The arrows keep doing what the theme already has them do —
// step the main carousel — and syncArrowBounds() disables them on the bounds.
//
// Swiper 11 is already on every PDP: layout/theme.liquid ships the full bundle
// (JS + CSS) for template.name == 'product', so nothing is fetched here.
//
// The strip container is deliberately NOT given the .swiper class:
// sections/custom-videos-gallary.liquid (present in templates/product.main.json)
// runs new Swiper('.swiper', ...) below 500px with a string selector, which resolves
// to the FIRST .swiper in the document — that would hand this strip to its config
// (slidesPerView: 1, loop, centeredSlides). Swiper adds no such class on its own and
// never looks the wrapper/slide classes up globally, so those are safe to reuse and
// the bundle's own CSS carries them. The .swiper base rules the container still needs
// are declared against .product__thumbnail-list in pdp-gallery-thumbnails.css instead.
(() => {
    const MOBILE_QUERY = '(max-width: 999px)';
    const SLIDES_PER_VIEW = 3;
    const SPACE_BETWEEN = 8;

    const mobile = window.matchMedia(MOBILE_QUERY);
    const swipers = new WeakMap();

    const getControls = (carousel) =>
        carousel.id && document.querySelector(`.product__media-nav[controls="${carousel.id}"]`);

    // Focal's <flickity-controls> never reflects the carousel bounds onto the prev/next
    // buttons. The stock layout hides that (the arrows sit in a row under the image),
    // but here they flank the thumbnail strip, so a dead arrow reads as broken.
    // The carousel has no wrapAround, so first/last slide are real bounds.
    const syncArrowBounds = async (carousel) => {
        const controls = getControls(carousel);

        if (!controls) return;

        // a single-slide carousel never builds a Flickity instance
        const flickity = await carousel.flickityInstance;

        if (!flickity) return;

        const prev = controls.querySelector('.product__media-prev-next[data-action="prev"]');
        const next = controls.querySelector('.product__media-prev-next[data-action="next"]');

        // variant filtering can drop slides, so read the count on every sync
        const lastIndex = flickity.slides.length - 1;

        if (prev) prev.disabled = flickity.selectedIndex <= 0;
        if (next) next.disabled = flickity.selectedIndex >= lastIndex;
    };

    // Filtered thumbnails are display:none (.product__media .is-filtered in
    // theme.css), and Swiper counts every wrapper child matching slideClass
    // whatever its display — a hidden one would still hold a slot and push the
    // last visible thumbnail out of reach. So the slide class tracks the visible
    // set instead of sitting on the markup. Returns how many are left, which is
    // what tells ensure() whether Swiper has to re-measure.
    const markSlides = (strip) => {
        let visibleCount = 0;

        strip.querySelectorAll('.product__thumbnail-item').forEach((item) => {
            const isVisible = !item.classList.contains('is-filtered');

            item.classList.toggle('swiper-slide', isVisible);
            if (isVisible) visibleCount++;
        });

        return visibleCount;
    };

    const destroy = (strip) => {
        const swiper = swipers.get(strip);

        if (swiper) {
            if (!swiper.destroyed) swiper.destroy(true, true);
            swipers.delete(strip);
        }

        strip.querySelector('.product__thumbnail-list-inner')?.classList.remove('swiper-wrapper');
        strip
            .querySelectorAll('.product__thumbnail-item')
            .forEach((item) => item.classList.remove('swiper-slide'));
    };

    const build = (strip) => {
        const wrapper = strip.querySelector('.product__thumbnail-list-inner');

        if (!wrapper || !window.Swiper) return null;

        wrapper.classList.add('swiper-wrapper');
        markSlides(strip);

        const swiper = new window.Swiper(strip, {
            slidesPerView: SLIDES_PER_VIEW,
            slidesPerGroup: 1,
            // On top of this, .product__thumbnail-item and .product__thumbnail each
            // carry 2px of padding, so the visible gap between two images is
            // SPACE_BETWEEN + 8. Paired with the strip's flex-basis in the CSS:
            // basis = 200 + 2 * SPACE_BETWEEN keeps the thumbnails at the size the
            // design calls for, since Swiper takes the gaps out of the width it has.
            spaceBetween: SPACE_BETWEEN,
            watchOverflow: true
            // no navigation module: the arrows in .product__media-nav belong to
            // <flickity-controls> and step the main carousel, not this strip
        });

        swipers.set(strip, swiper);
        return swiper;
    };

    // Returns the live Swiper for a strip, building or tearing it down as needed.
    // Desktop keeps Focal's own thumbnail column, so the strip is mobile-only.
    const ensure = (strip) => {
        if (!mobile.matches) {
            destroy(strip);
            return null;
        }

        const swiper = swipers.get(strip);

        if (!swiper || swiper.destroyed) {
            return build(strip);
        }

        // Variant filtering adds and drops thumbnails, and update() re-reads the
        // slide set off the DOM, so the strip never has to be rebuilt for that.
        // It also re-measures everything and can re-snap the translate, so it only
        // runs when the visible set actually changed — this same path is hit on
        // every slide change, where nothing has.
        if (markSlides(strip) !== swiper.slides.length) {
            swiper.update();
        }

        return swiper;
    };

    // slideTo() left-aligns its target, which would shunt the strip on every step.
    // The active thumbnail is only chased once it has actually fallen outside the
    // three on screen, so walking the gallery advances the strip one at a time.
    const revealSlide = (swiper, slide) => {
        const index = slide ? swiper.slides.indexOf(slide) : -1;

        if (index < 0) return;

        const first = swiper.activeIndex;
        const last = first + SLIDES_PER_VIEW - 1;

        if (index < first) {
            swiper.slideTo(index);
        } else if (index > last) {
            swiper.slideTo(index - SLIDES_PER_VIEW + 1);
        }
    };

    // The active thumbnail is matched on the carousel's own selected media rather
    // than on [aria-current]: FlickityControls._onSlideChanged() sets that
    // attribute only after awaiting flickityInstance, so it is still a tick behind
    // when this runs off the same event.
    const syncStrip = async (carousel) => {
        const strip = getControls(carousel)?.querySelector('.product__thumbnail-list');

        if (!strip) return;

        const swiper = ensure(strip);

        if (!swiper) return;

        const flickity = await carousel.flickityInstance;
        const mediaId = flickity?.selectedElement?.getAttribute('data-media-id');

        if (!mediaId) return;

        revealSlide(swiper, strip.querySelector(`.product__thumbnail-item[data-media-id="${mediaId}"]`));
    };

    const sync = (carousel) => {
        syncStrip(carousel);
        syncArrowBounds(carousel);
    };

    // Goes through the carousels rather than the strips so the active thumbnail is
    // scrolled into view too: a variant-preselected media is not slide 0.
    const syncAll = () => {
        document.querySelectorAll('.product__media-nav[controls]').forEach((controls) => {
            const carousel = document.getElementById(controls.getAttribute('controls'));

            if (carousel) sync(carousel);
        });
    };

    try {
        // Flickity events bubble from <flickity-carousel>, so these delegated listeners
        // also cover carousels rendered after this script runs. 'flickity:ready' fires
        // again after reload(), which is how the theme rebuilds the carousel when a
        // variant filters media out.
        document.addEventListener('flickity:ready', (event) => sync(event.target));
        document.addEventListener('flickity:slide-changed', (event) => sync(event.target));

        // crossing the breakpoint has to build the strip or tear it back down
        mobile.addEventListener('change', syncAll);

        // covers carousels whose 'flickity:ready' fired before these listeners existed
        syncAll();
    } catch (error) {
        console.error('Error setting up PDP gallery thumbnails:', error);
    }
})();
